// Bullogic revenue engine (rebuild). Pure and timezone-safe: no React, no DB, no
// browser APIs, and it never calls `new Date(x).getMonth()` in local time (that is
// the bug that rolled a March 1 start back to February). Everything here is a plain
// function of its inputs, so it is trivially testable.
//
// It computes each engagement's 12-month contribution for a given calendar year.
// Engagement shape (produced by mapClientToEngagements, or later by the CRM):
//   { division, category, pattern, ...pattern-specific fields, startDate, endDate }
// Patterns (see REVENUE_ENGINE_SPEC.md section 3):
//   'recurring' | 'aum' | 'committed' | 'actuals'

export const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
export const AUM_FEE_RATE = 0.005;              // 0.5% of assets under management (firm setting)
export const AUM_PAYMENT_MONTHS = [0, 3, 6, 9]; // Jan, Apr, Jul, Oct: AUM is billed one quarter in arrears

// Read {year, month0} from a date by its literal calendar digits. Accepts
// 'YYYY-MM-DD', a full ISO string, or a Date. No timezone math, so '2026-03-01'
// is always March (month0 = 2), regardless of the machine's timezone.
export function calYearMonth(dateVal) {
  if (!dateVal) return null;
  const s = typeof dateVal === 'string' ? dateVal : new Date(dateVal).toISOString();
  const m = /^(\d{4})-(\d{2})/.exec(String(s).slice(0, 10));
  return m ? { year: Number(m[1]), month0: Number(m[2]) - 1 } : null;
}

// The month indices [0..11] an engagement is active during `year`, narrowed by a
// mid-year start (from January) and a mid-year end (to December).
export function activeMonthIndices(eng, year) {
  const start = calYearMonth(eng.startDate);
  const end = calYearMonth(eng.endDate);
  let first = 0, last = 11;
  if (start) {
    if (start.year > year) return [];
    if (start.year === year) first = start.month0;
  }
  if (end) {
    if (end.year < year) return [];
    if (end.year === year) last = end.month0;
  }
  const out = [];
  for (let m = first; m <= last; m++) out.push(m);
  return out;
}

// The monthly rate in effect for (year, month0), walking effective-dated rate
// history. Each history entry is { effectiveDate: 'YYYY-MM-DD', monthlyRevenue }.
// A month uses the latest entry whose date is on or before the month's last day.
export function rateForMonth(eng, year, month0) {
  const base = Number(eng.monthlyRate) || 0;
  const hist = Array.isArray(eng.rateHistory) ? eng.rateHistory : [];
  if (!hist.length) return base;
  const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const monthEnd = `${year}-${String(month0 + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
  let bestDate = null, rate = base;
  for (const h of hist) {
    if (!h?.effectiveDate || h.effectiveDate > monthEnd) continue;
    if (!bestDate || h.effectiveDate > bestDate) { bestDate = h.effectiveDate; rate = Number(h.monthlyRevenue) || 0; }
  }
  return rate;
}

// The annual AUM fee for an engagement: an explicit fee, or asset amount x rate.
export function aumAnnualFee(eng) {
  if (eng.annualFee != null) return Number(eng.annualFee) || 0;
  return (Number(eng.assetAmount) || 0) * (eng.feeRate != null ? eng.feeRate : AUM_FEE_RATE);
}

// One engagement's contribution for `year`: 12 monthly values plus the annual sum.
export function engagementContribution(eng, year) {
  const months = new Array(12).fill(0);
  switch (eng.pattern) {
    case 'recurring': {
      // Monthly-equivalent rate on every active month (rate-history aware). Pay
      // frequency (monthly / quarterly / annual) does not change this even
      // projection; QuickBooks actuals reconcile the real cadence.
      for (const m of activeMonthIndices(eng, year)) months[m] += rateForMonth(eng, year, m);
      break;
    }
    case 'aum': {
      // Annual fee / 4, billed one quarter in arrears (Jan, Apr, Jul, Oct). For an
      // engagement signed during the year, only the payment months on or after the
      // sign month count, with no intra-quarter proration.
      const perQuarter = aumAnnualFee(eng) / 4;
      const start = calYearMonth(eng.startDate);
      const end = calYearMonth(eng.endDate);
      const signedThisYear = start && start.year === year;
      if (!(end && end.year < year)) {
        for (const pm of AUM_PAYMENT_MONTHS) {
          if (signedThisYear && pm < start.month0) continue;
          if (end && end.year === year && pm > end.month0) continue;
          months[pm] += perQuarter;
        }
      }
      break;
    }
    case 'committed': {
      // Committed to a revenue year, absorbed by actuals over time. As a pure
      // projection (no actuals supplied) the amount spreads evenly across the year;
      // the QuickBooks reconciliation layer later replaces closed months with real
      // payments and re-spreads the remainder (spec section 6).
      const ry = eng.revenueYear != null ? eng.revenueYear : year;
      if (ry === year) {
        const per = (Number(eng.amount) || 0) / 12;
        for (let m = 0; m < 12; m++) months[m] += per;
      }
      break;
    }
    case 'actuals':
    default:
      break; // commissions and unknown patterns are not projected
  }
  const annual = months.reduce((a, b) => a + b, 0);
  return { months, annual };
}

// Sum a set of engagements for `year` into { months[12], annual }.
export function sumEngagements(engagements, year) {
  const months = new Array(12).fill(0);
  for (const eng of engagements) {
    const c = engagementContribution(eng, year);
    for (let m = 0; m < 12; m++) months[m] += c.months[m];
  }
  return { months, annual: months.reduce((a, b) => a + b, 0) };
}
